
import React, { useEffect, useState } from 'react';
import { TrendingUp } from 'lucide-react';

const ProgressOverview = ({ today, overall }) => {
    const [animatedToday, setAnimatedToday] = useState(0);
    const [animatedOverall, setAnimatedOverall] = useState(0);

    const todayPercent = today?.asOfDatePercent ?? today?.percentage ?? 0;
    const todayTrend = today?.dayOverDayChangePercent ?? today?.trend ?? 0;
    const overallPercent = overall?.overallPercent ?? overall?.percentage ?? 0;

    useEffect(() => {
        const duration = 1000; // Animation duration in ms
        const frameRate = 60;
        const totalFrames = Math.round(duration * frameRate / 1000);

        let frame = 0;

        const counter = setInterval(() => {
            frame++;
            const progress = frame / totalFrames;

            // Ease out cubic function for smoother animation
            const easeOut = 1 - Math.pow(1 - progress, 3);

            if (frame <= totalFrames) {
                setAnimatedToday(Math.round(todayPercent * easeOut));
                setAnimatedOverall(Math.round(overallPercent * easeOut));
            } else {
                setAnimatedToday(todayPercent);
                setAnimatedOverall(overallPercent);
                clearInterval(counter);
            }
        }, 1000 / frameRate);

        return () => clearInterval(counter);
    }, [todayPercent, overallPercent]);


    // Generate description if not provided
    let description = today?.description;
    if (!description && today?.dts?.length > 0) {
        const completedCount = today.dts.filter(t => t.status === 'Completed').length;
        const totalCount = today.dts.length;
        description = `${completedCount} of ${totalCount} tasks completed today.`;
    }

    return (
        <div className="flex flex-col h-full">
            {/* Header Tabs */}
            <div className="flex mb-6">
                <div
                    className="bg-[#D4B95E] text-white px-10 py-3 font-medium text-base relative z-10 pr-16"
                    style={{ clipPath: 'polygon(0 0, 100% 0, 85% 100%, 0% 100%)' }}
                >
                    Progress Overview
                </div>
                <div
                    className="bg-[#1E4C56] text-white px-10 py-3 font-medium text-base -ml-10 pl-16 relative z-0 pr-16"
                    style={{ clipPath: 'polygon(0 0, 100% 0, 85% 100%, 0% 100%)' }}
                >
                    Minor Delays
                </div>
            </div>
            {/* Main Content Area with Split Top and Unified Bottom */}
            <div className="flex flex-col">
                {/* Top Split Section */}
                <div className="flex gap-4 md:gap-8 items-stretch h-full">
                    {/* TODAY Card Top */}
                    <div className="bg-white rounded-tl-2xl rounded-tr-2xl p-6 pb-6 flex flex-col items-center flex-1 relative shadow-md">
                        <div className="text-[24px] font-semibold text-black uppercase mb-3 leading-none self-start">TODAY</div>
                        <div className="relative w-36 h-36">
                            <svg className="w-full h-full transform -rotate-90">
                                <circle
                                    cx="72"
                                    cy="72"
                                    r="64"
                                    stroke="#1F4B55"
                                    strokeWidth="8"
                                    fill="transparent"
                                />
                                <circle
                                    cx="72"
                                    cy="72"
                                    r="64"
                                    stroke="#D4B95E"
                                    strokeWidth="8"
                                    fill="transparent"
                                    strokeDasharray={2 * Math.PI * 64}
                                    strokeDashoffset={2 * Math.PI * 64 * (1 - animatedToday / 100)}
                                    strokeLinecap="round"
                                    style={{ transition: 'stroke-dashoffset 0.1s linear' }}
                                />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className="text-4xl font-bold text-black">{animatedToday}%</span>
                            </div>
                        </div>

                        {/* Trend Indicator */}
                        <div className="absolute bottom-4 right-6 flex items-end">
                            <TrendingUp size={24} className="text-black mb-1" />
                            <span className="text-xl font-bold text-black ml-1">{todayTrend}%</span>
                        </div>
                    </div>

                    {/* OVERALL Card Top */}
                    <div className="bg-white rounded-tl-2xl rounded-tr-2xl p-6 pb-0 flex-1 flex flex-col shadow-md">
                        <div className="text-[24px] font-semibold text-black uppercase mb-3 leading-none">OVERALL</div>

                        <div className="flex items-end justify-between mb-3">
                            <span className="text-5xl font-bold text-black">{animatedOverall}%</span>
                            <span className="text-sm text-gray-800 font-medium mb-2">Complete</span>
                        </div>

                        <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                                className="bg-[#1F4B55] h-3 rounded-full"
                                style={{
                                    width: `${animatedOverall}%`,
                                    transition: 'width 0.1s linear'
                                }}
                            ></div>
                        </div>
                    </div>
                </div>

                {/* Unified Bottom Section */}
                <div className="bg-white rounded-b-2xl rounded-tr-2xl rounded-tl-2xl p-6 pt-4 -mt-4 z-0 relative shadow-md">
                    <p className="text-sm text-gray-700 leading-relaxed max-w-2xl">
                        {description || 'No description available for today.'}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ProgressOverview;
